import {useEffect, useState} from 'react'
import {OrderModel} from '../models/OrderModel'
import {OrderStatus} from '../constants'

const PREPARING_MESSAGES = [
  'ORDER.PROCESSING.UPLOADING_IMAGES',
  'ORDER.PROCESSING.ANALYSING_IMAGES',
  'ORDER.PROCESSING.PREPROCESSING_IMAGES',
]

const PROCESSING_MESSAGES = [
  'ORDER.PROCESSING.AI_PROCESSING_IMAGES',
  'ORDER.PROCESSING.SERVER_OVERLOAD_MESSAGE',
]

/**
 * Manages the sequential AI processing text and progress percentage.
 */
export function useAiProcessing(order: OrderModel | undefined) {
  const [messageIndex, setMessageIndex] = useState(0)
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(null)

  const isProcessing = order?.status === OrderStatus.AI_PROCESSING
  const progress =
    order && order.totalItems > 0
      ? Math.round((order.completedItems / order.totalItems) * 100)
      : 0

  useEffect(() => {
    if (!isProcessing) {
      setProcessingStartTime(null)
      setMessageIndex(0)
      return
    }

    if (processingStartTime === null) {
      setProcessingStartTime(Date.now())
    }

    if (order!.completedItems === 0) {
      const interval = setInterval(() => {
        setMessageIndex((prev) => Math.min(prev + 1, PREPARING_MESSAGES.length - 1))
      }, 3000)
      return () => clearInterval(interval)
    } else {
      setMessageIndex(0)
      const timeThreshold = order!.totalItems <= 10 ? 60000 : 180000
      const elapsed = processingStartTime ? Date.now() - processingStartTime : 0

      if (elapsed > timeThreshold) {
        setMessageIndex(1)
      } else {
        const timeout = setTimeout(() => setMessageIndex(1), timeThreshold - elapsed)
        return () => clearTimeout(timeout)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, order?.completedItems, order?.totalItems, processingStartTime])

  const messageId = isProcessing
    ? order!.completedItems === 0
      ? PREPARING_MESSAGES[Math.min(messageIndex, PREPARING_MESSAGES.length - 1)]
      : PROCESSING_MESSAGES[Math.min(messageIndex, PROCESSING_MESSAGES.length - 1)]
    : undefined

  return {isProcessing, progress, messageId}
}
